import { Injectable, NgZone } from '@angular/core';
import {
  arrayUnion,
  collection,
  collectionData,
  Firestore,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import { BehaviorSubject, firstValueFrom, lastValueFrom, Observable, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { ChatNotificationService } from 'src/app/services/chat-notification.service';
import { ChatService } from 'src/app/services/chat.service';
import { UserStatusDto } from 'src/app/services/user-status.service';
import { Role } from 'src/app/shared/role';
import { environment } from 'src/environments/environment';

export interface VoiceMessage {
  id: string;
  senderId: string;
  senderName: string;
  isMine: boolean;
  sentAt: Date;
  // Unknown (undefined) for legacy Voice Box voices until they are played.
  durationSec?: number;
  // Present for voices sent through Voice Comms; played through the API.
  voiceId?: string;
  // Present for legacy Voice Box voices; played from the stored blob url.
  url?: string;
  deleted?: boolean;
  unread?: boolean;
  // Local-only state for an optimistic bubble that has not been confirmed by the server yet.
  sending?: boolean;
  sendFailed?: boolean;
}

export interface VoiceRecipient {
  id: string;
  name: string;
  kind: 'person' | 'team';
  subtitle?: string;
  photo?: string;
  status?: UserStatusDto;
  unread: number;
  memberCount?: number;
  onDuty?: boolean;
  // Person with a 1-1 voice conversation with the current user (their unread state is tracked).
  hasConversation?: boolean;
}

export interface VoiceRecipients {
  people: VoiceRecipient[];
  teams: VoiceRecipient[];
}

export const DEFAULT_AVATAR = 'assets/avt/avt1.png';
export const GROUP_AVATAR = 'assets/icons/group.png';

export function avatarFor(recipient: VoiceRecipient): string {
  if (recipient.kind === 'team') return GROUP_AVATAR;
  return recipient.photo || DEFAULT_AVATAR;
}

export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r < 10 ? '0' + r : r}`;
}

const REFRESH_INTERVAL_MS = 120000;
const MIN_REFRESH_GAP_MS = 5000;
const LIVE_PAGE_SIZE = 20;
const OLDER_PAGE_SIZE = 20;
const MARK_READ_WINDOW = 100;

const EXTENSIONS: { [mime: string]: string } = {
  webm: 'webm',
  aac: 'aac',
  mp4: 'm4a',
  m4a: 'm4a',
  mpeg: 'mp3',
  mp3: 'mp3',
  ogg: 'ogg',
  wav: 'wav',
};

@Injectable({ providedIn: 'root' })
export class VoiceCommsService {
  private watchedUserId?: string;
  private refreshTimer?: any;
  private refreshPromise?: Promise<VoiceRecipients>;
  private lastRefreshAt = 0;
  private personSubs = new Map<string, Subscription>();
  private personUnreadMap: { [userId: string]: number } = {};
  private audioUrls = new Map<string, Promise<string>>();
  private audioUrlsUserId?: string;

  private recipientsSubject = new BehaviorSubject<VoiceRecipients>({ people: [], teams: [] });

  recipients$ = this.recipientsSubject.asObservable();
  personUnread$ = new BehaviorSubject<{ [userId: string]: number }>({});

  constructor(
    private api: ApiService,
    private auth: AuthService,
    private chat: ChatService,
    private chatNotification: ChatNotificationService,
    private firestore: Firestore,
    private zone: NgZone
  ) {}

  private get me(): string {
    return this.auth.currentUserValue?.user_id as string;
  }

  get isKnight(): boolean {
    const roles = (this.auth.currentUserValue?.roles || '').split(',').map((r) => r.trim());
    return roles.includes(Role.Knight) || roles.includes(Role.KnightLeader);
  }

  private get base(): string {
    return this.isKnight ? '/voicecomms/knight' : '/voicecomms';
  }

  photoUrl(userId: string, photo?: string): string | undefined {
    return photo ? `${environment.apiEndpoint}/user/media/thumbnail/${userId}/${photo}` : undefined;
  }

  // Loads the recipient lists now and keeps refreshing them, with the per-person unread listeners.
  // Safe to call from several places: only one refresh timer runs per signed-in user.
  async startWatching(): Promise<void> {
    if (!this.me) return;
    if (this.watchedUserId !== this.me) {
      this.stopWatching();
      this.watchedUserId = this.me;
      this.chatNotification.init();
      this.refreshTimer = setInterval(() => this.refreshRecipients().catch(() => {}), REFRESH_INTERVAL_MS);
    }
    await this.refreshRecipients().catch(() => {});
  }

  stopWatching() {
    clearInterval(this.refreshTimer);
    this.personSubs.forEach((s) => s.unsubscribe());
    this.personSubs.clear();
    this.personUnreadMap = {};
    this.personUnread$.next({});
    this.recipientsSubject.next({ people: [], teams: [] });
    this.refreshPromise = undefined;
    this.lastRefreshAt = 0;
    this.watchedUserId = undefined;
  }

  // Callers that start together (sidebar, page, timer) share one request instead of each hitting the
  // API; force skips only the short "just refreshed" gap, never a request already in flight.
  refreshRecipients(force = false): Promise<VoiceRecipients> {
    if (this.refreshPromise) return this.refreshPromise;
    if (!force && Date.now() - this.lastRefreshAt < MIN_REFRESH_GAP_MS) return Promise.resolve(this.recipientsSubject.value);

    this.refreshPromise = this.loadRecipients().finally(() => {
      this.refreshPromise = undefined;
      this.lastRefreshAt = Date.now();
    });
    return this.refreshPromise;
  }

  private async loadRecipients(): Promise<VoiceRecipients> {
    const res = await firstValueFrom(this.api.get(`${this.base}/recipients?isNoLoading=true`));

    const previous = this.recipientsSubject.value;
    const previousById = new Map([...previous.people, ...previous.teams].map((r) => [r.id, r]));

    const people: VoiceRecipient[] = (res.people || []).map((p: any) => {
      const existing = previousById.get(p.userId);
      const item: VoiceRecipient = {
        id: p.userId,
        name: p.fullName || p.email || 'Unknown',
        kind: 'person',
        subtitle: p.email || '',
        photo: this.photoUrl(p.userId, p.photo),
        status: { userId: p.userId, name: p.fullName, status: 'Offline', lastUpdated: null },
        unread: existing?.unread ?? 0,
        hasConversation: !!p.hasConversation,
      };
      return item;
    });

    let teamSource: any[] = res.teams || [];
    if (this.isKnight) {
      const groups: any[] = (await firstValueFrom(this.chat.getAllGroupsWithouLoading())) || [];
      teamSource = groups.map((g) => ({ groupId: g.id, name: g.group, memberCount: g.memberCount }));
    }
    const teams: VoiceRecipient[] = teamSource.map((t: any) => ({
      id: t.groupId,
      name: t.name || 'Group',
      kind: 'team',
      unread: previousById.get(t.groupId)?.unread ?? 0,
      memberCount: t.memberCount,
      onDuty: !!t.onDuty,
    }));

    const next = { people, teams };
    this.syncPersonListeners(people);
    this.recipientsSubject.next(next);

    return next;
  }

  // Unread counts of persons come from readBy on their 1-1 ptt collection; teams reuse the counter
  // that Chat already keeps for group ptt.
  private syncPersonListeners(people: VoiceRecipient[]) {
    const wanted = new Set(people.filter((p) => p.hasConversation).map((p) => p.id));

    this.personSubs.forEach((sub, id) => {
      if (!wanted.has(id)) {
        sub.unsubscribe();
        this.personSubs.delete(id);
        delete this.personUnreadMap[id];
      }
    });

    wanted.forEach((id) => {
      if (this.personSubs.has(id)) return;
      const chatId = this.chat.getChatId(this.me, id);
      const ref = collection(this.firestore, `chats/${chatId}/ptt`);
      const sub = collectionData(query(ref, orderBy('timestamp', 'desc'), limit(30))).subscribe((docs: any[]) => {
        this.personUnreadMap[id] = docs.filter((d) => this.isUnread(d)).length;
        this.personUnread$.next({ ...this.personUnreadMap });
      });
      this.personSubs.set(id, sub);
    });

    this.personUnread$.next({ ...this.personUnreadMap });
  }

  private isUnread(d: any): boolean {
    return !d.isDeleted && d.user !== this.me && !(d.readBy || []).includes(this.me);
  }

  get teamUnread$(): Observable<{ [groupId: string]: number }> {
    return this.chatNotification.unreadPttCount$;
  }

  hasUnread$(): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      let person = false;
      let team = false;
      const emit = () => subscriber.next(person || team);
      const subs = [
        this.personUnread$.subscribe((m) => {
          person = Object.values(m).some((n) => n > 0);
          emit();
        }),
        this.chatNotification.hasNewMessagePtt$.subscribe((m) => {
          team = Object.values(m).some((v) => v === true);
          emit();
        }),
      ];
      return () => subs.forEach((s) => s.unsubscribe());
    });
  }

  // MESSAGES //////////

  private pttCollection(recipient: VoiceRecipient) {
    const path =
      recipient.kind === 'team'
        ? `group/${recipient.id}/ptt`
        : `chats/${this.chat.getChatId(this.me, recipient.id)}/ptt`;
    return collection(this.firestore, path);
  }

  private toMessage(d: any): VoiceMessage {
    const timestamp = d.timestamp?.toDate ? d.timestamp.toDate() : d.timestamp;
    return {
      id: d.id,
      senderId: d.user,
      senderName: d.userName || 'Unknown',
      isMine: d.user === this.me,
      sentAt: timestamp ?? new Date(),
      durationSec: d.durationSec || undefined,
      voiceId: d.voiceId,
      url: d.url,
      deleted: !!d.isDeleted,
      unread: this.isUnread(d),
    };
  }

  private toMessages(docs: any[]): VoiceMessage[] {
    return docs.filter((d) => d.voiceId || d.url).map((d) => this.toMessage(d));
  }

  get livePageSize(): number {
    return LIVE_PAGE_SIZE;
  }

  get olderPageSize(): number {
    return OLDER_PAGE_SIZE;
  }

  // Realtime window of the most recent voices (oldest first). count is the number of documents the
  // window holds and fromCache tells whether the snapshot is still local data: only a server snapshot
  // with fewer documents than a page proves that there is no older history.
  listenMessages(recipient: VoiceRecipient): Observable<{ items: VoiceMessage[]; count: number; fromCache: boolean }> {
    const q = query(this.pttCollection(recipient), orderBy('timestamp', 'desc'), limit(LIVE_PAGE_SIZE));

    return new Observable((subscriber) =>
      onSnapshot(
        q,
        { includeMetadataChanges: true },
        (snapshot) => {
          const docs = snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
          this.zone.run(() =>
            subscriber.next({
              items: this.toMessages(docs).reverse(),
              count: docs.length,
              fromCache: snapshot.metadata.fromCache,
            })
          );
        },
        (error) => this.zone.run(() => subscriber.error(error))
      )
    );
  }

  // Voices older than the given one, oldest first, ready to be prepended.
  async loadOlder(recipient: VoiceRecipient, oldest: VoiceMessage): Promise<{ items: VoiceMessage[]; count: number }> {
    const q = query(
      this.pttCollection(recipient),
      orderBy('timestamp', 'desc'),
      where('timestamp', '<', oldest.sentAt),
      limit(OLDER_PAGE_SIZE)
    );
    const snapshot = await getDocs(q);
    const docs = snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));

    return { items: this.toMessages(docs).reverse(), count: docs.length };
  }

  // Marks the unread voices of the most recent window as read (a single batch stays far below the
  // 500 writes limit and the whole collection is never read).
  async markRead(recipient: VoiceRecipient): Promise<void> {
    const snapshot = await getDocs(
      query(this.pttCollection(recipient), orderBy('timestamp', 'desc'), limit(MARK_READ_WINDOW))
    );

    const batch = writeBatch(this.firestore);
    let pending = 0;
    snapshot.forEach((docSnap) => {
      const data: any = docSnap.data();
      if (!(data.readBy || []).includes(this.me)) {
        batch.update(docSnap.ref, { readBy: arrayUnion(this.me) });
        pending++;
      }
    });
    if (pending) await batch.commit();
  }

  // SEND / DELETE //////////

  // Uploads the recording; the API validates, encrypts, stores it and writes the Firestore message
  // document. Returns an error message, or null when the voice was sent.
  async sendVoice(recipient: VoiceRecipient, blob: Blob, durationSec: number): Promise<string | null> {
    try {
      const mimeSub = (blob.type || 'audio/webm').split(';')[0].split('/').pop() || 'webm';
      const extension = EXTENSIONS[mimeSub] || 'webm';
      const file = new File([blob], `voice-${uuidv4()}.${extension}`, { type: blob.type || 'audio/webm' });

      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('durationSec', String(durationSec));
      formData.append(recipient.kind === 'team' ? 'groupId' : 'toUserId', recipient.id);

      await lastValueFrom(this.api.postFile(`${this.base}/upload?isNoLoading=true`, formData));

      return null;
    } catch (ex: any) {
      return ex?.message || 'Unable to send the voice message';
    }
  }

  async deleteVoice(message: VoiceMessage): Promise<string | null> {
    try {
      await lastValueFrom(this.api.post('/voicecomms/delete?isNoLoading=true', { voiceId: message.voiceId }));
      return null;
    } catch (ex: any) {
      return ex?.message || 'Unable to delete the voice message';
    }
  }

  // AUDIO //////////

  // Every visible voice bubble asks to download+decrypt its audio at once (IntersectionObserver, one
  // request per bubble). Left unbounded, a page with many voices fires more requests than the browser's
  // per-origin connection limit (6 on HTTP/1.1), so most of them just sit "Stalled" waiting for a free
  // connection instead of actually loading. Capping how many run at a time keeps the rest queued here
  // instead, so each one that does run finishes quickly.
  private static readonly MAX_CONCURRENT_AUDIO_DOWNLOADS = 3;
  private activeAudioDownloads = 0;
  private audioDownloadQueue: (() => void)[] = [];

  private acquireAudioDownloadSlot(): Promise<void> {
    if (this.activeAudioDownloads < VoiceCommsService.MAX_CONCURRENT_AUDIO_DOWNLOADS) {
      this.activeAudioDownloads++;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => this.audioDownloadQueue.push(resolve)).then(() => {
      this.activeAudioDownloads++;
    });
  }

  private releaseAudioDownloadSlot(): void {
    this.activeAudioDownloads--;
    this.audioDownloadQueue.shift()?.();
  }

  // Each voice is downloaded once per session; the API checks permission and decrypts it.
  resolveAudioUrl(voiceId: string): Promise<string> {
    if (this.audioUrlsUserId !== this.me) {
      this.audioUrls.clear();
      this.audioUrlsUserId = this.me;
    }

    let cached = this.audioUrls.get(voiceId);
    if (!cached) {
      //       cached = lastValueFrom(this.api.downloadFile(`${this.base}/audio/${voiceId}?isNoLoading=true`)).then((blob) =>
      //   URL.createObjectURL(blob)
      // );

      cached = this.downloadAudio(voiceId);
      this.audioUrls.set(voiceId, cached);
      cached.catch(() => this.audioUrls.delete(voiceId));
    }
    return cached;
  }

  private async downloadAudio(voiceId: string): Promise<string> {
    await this.acquireAudioDownloadSlot();
    try {
      const blob = await lastValueFrom(this.api.downloadFile(`${this.base}/audio/${voiceId}?isNoLoading=true`));
      return URL.createObjectURL(blob);
    } finally {
      this.releaseAudioDownloadSlot();
    }
  }
}
